/**
 * Calculator Input — `initInputTooltips`, `formatInputField`, `formatInputsOnLoad`.
 */

import { getBreakpointMediaQuery } from 'js/constants.js';
import { formatNumber } from '../../../../container/clientlibs/site/js/format.js';

const TOOLTIP_EDGE = 16;
const TOOLTIP_LEFT_MAX = 250;
const TOOLTIP_CENTER_MAX = 320;

/** Hover/focus for `.calc-input__tooltip-container` (placement in input.less). */
export function initInputTooltips(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;

    const tooltipContainers = root.querySelectorAll('.calc-input__tooltip-container');
    const viewportFit = (max) => Math.min(max, window.innerWidth - TOOLTIP_EDGE * 2);

    tooltipContainers.forEach((container) => {
        const tooltip = container.querySelector('.calc-input__tooltip');
        if (!tooltip) return;

        const positionTooltip = () => {
            const rect = container.getBoundingClientRect();
            // [LEGACY BREAKPOINT: under 768px -> getBreakpointMediaQuery('down', 'small')]
            const isMobile = getBreakpointMediaQuery('down', 'small').matches;
            const centeredCap = `${viewportFit(TOOLTIP_CENTER_MAX)}px`;
            const maxFitLeft = Math.max(rect.left - TOOLTIP_EDGE - 8, 0);

            const showLeft = (widthExpr) => {
                tooltip.classList.add('tooltip-left');
                tooltip.style.maxWidth = widthExpr;
            };

            if (isMobile) {
                showLeft(`${viewportFit(TOOLTIP_LEFT_MAX)}px`);
                return;
            }
            if (rect.right > window.innerWidth - 160) {
                showLeft(`${Math.min(TOOLTIP_LEFT_MAX, maxFitLeft)}px`);
                return;
            }
            if (rect.left < 160) {
                tooltip.classList.remove('tooltip-left');
                tooltip.style.maxWidth = centeredCap;
                return;
            }
            tooltip.classList.remove('tooltip-left');
            tooltip.style.removeProperty('max-width');
        };

        const show = () => {
            positionTooltip();
            tooltip.style.opacity = '1';
            tooltip.style.visibility = 'visible';
        };

        const hide = () => {
            tooltip.style.opacity = '0';
            tooltip.style.visibility = 'hidden';
            tooltip.classList.remove('tooltip-left');
            tooltip.style.removeProperty('max-width');
        };

        container.addEventListener('mouseenter', show);
        container.addEventListener('mouseleave', hide);
        container.addEventListener('focusin', show);
        container.addEventListener('focusout', (e) => {
            if (!container.contains(e.relatedTarget)) {
                hide();
            }
        });
    });
}

/**
 * Stash the input's raw numeric value on `data-raw-value`, swap the
 * field to `type="text"`, and render the formatted display value.
 *
 * Returns false (and leaves the field untouched) when there is no
 * value to format or the field is configured as `plain` (no
 * formatting). The caller can rely on that return value to short-
 * circuit empty-on-load handling.
 */
export function formatInputField(input, locale) {
    const wrapper = input.closest('.calc-input');
    const formatKey = wrapper ? (wrapper.getAttribute('data-format') || 'integer') : 'integer';
    if (formatKey === 'plain') return false;
    const raw = input.value;
    if (!raw && raw !== '0') return false;
    input.setAttribute('data-raw-value', raw);
    input.type = 'text'; // eslint-disable-line no-param-reassign
    input.value = formatNumber(parseFloat(raw) || 0, formatKey, locale); // eslint-disable-line no-param-reassign
    return true;
}

/**
 * Format every input on initial page load. Skips empty fields and
 * authored "0" defaults so blank inputs stay blank rather than
 * showing a literal "0" before the visitor types.
 */
export function formatInputsOnLoad(inputs, locale) {
    inputs.forEach((input) => {
        if (!input.value || input.value === '0') return;
        formatInputField(input, locale);
    });
}
